import {FC, useCallback, useContext} from 'react'
import File from "Entity/File";
import Product from "Entity/Product";
import Choice from "Entity/Property/Choice";
import CardData from "Entity/Collection/ListingSetting/View/CardData";
import {CollectionContext} from "Vendor/Context/CollectionContextProvider";
import useTranslationDataUtils from "Vendor/Utils/TranslationDataUtils";
import PreviewCanvas from "./_PreviewCanvas";
import PreviewImage from "./_PreviewImage";
import {buildFolderTree, FolderGrouping, PreviewEntry} from "./BimboShareFolders";

type SectionPayload = { item: File | Product }

type BimboSharePreviewModalProps = {
    show: boolean
    handleClose: () => void
    title: string
    entity: 'bucket' | 'catalog'
    items: (File | Product)[]
    cardDatas: CardData[]
    grouping: FolderGrouping
    startIndex: number
}

const BimboSharePreviewModal: FC<BimboSharePreviewModalProps> = ({
    show,
    handleClose,
    title,
    entity,
    items,
    cardDatas,
    grouping,
    startIndex,
}) => {
    const {translate} = useTranslationDataUtils()
    const {getValue} = useContext(CollectionContext)

    const getName = useCallback((item: File | Product) => {
        return entity === 'bucket' ? (item as File).name : translate(item, 'name')
    }, [entity, translate])

    const choiceLabel = useCallback((choice: Choice) => {
        const label = translate(choice, 'label')

        return label !== '--unknown--' ? label : choice.value ?? 'no value and no label'
    }, [translate])

    const tree = buildFolderTree(items, grouping, choiceLabel, getName)
    const startKey = tree.entries.find(entry => (entry.payload.item as File | Product) === items[startIndex])?.key

    const renderImage = (entry: PreviewEntry<SectionPayload>) =>
        <PreviewImage src={entry.payload.item.thumbnail?.replaceAll('__SIZING__', 'extra-large')} alt={entry.name}/>

    const renderDetails = (entry: PreviewEntry<SectionPayload>) => {
        const properties = cardDatas
            .filter(cardData => cardData.property)
            .map(cardData => ({
                slug: cardData.property!.slug,
                label: translate(cardData.property!, 'label'),
                value: getValue(entry.payload.item, cardData.property!),
            }))
            .filter(property => property.value !== undefined && property.value !== '')

        return <>
            <h6 className="name">{entry.name}</h6>
            {properties.length > 0 &&
                <dl>
                    {properties.map(property =>
                        <div key={property.slug}>
                            <dt>{property.label}</dt>
                            <dd>{property.value}</dd>
                        </div>
                    )}
                </dl>
            }
        </>
    }

    return <PreviewCanvas
        show={show}
        handleClose={handleClose}
        title={title}
        tree={tree}
        startKey={startKey}
        renderImage={renderImage}
        renderDetails={renderDetails}
    />
}

export default BimboSharePreviewModal
